import { Component, forwardRef, Injector, Input, Optional } from '@angular/core';
import { ControlContainer, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * @group Forms
 * @component Radio
 *
 * @export
 * @class LsqRadioComponent
 * @extends {BaseFormControlComponent<boolean>}
 * @implements {AfterContentInit}
 * @property {string} id Typically there are more than 1 item set with radio buttons so a unique id will need to be specified.
 * @property {string} label The radio buttons label component.
 * @property {string} value The value that will be assigned to the model.
 * @property {string} [name] This will set the input's name.
 */
@Component({
    selector: 'lsq-radio',
    template: `
        <div [id]="createId()" [class.disabled]="disabled" [class.radio-container]="true">
            <label class="radio-label">
                <input
                    [class.selected]="!model"
                    [id]="createId('input')"
                    [attr.name]="name"
                    [attr.type]="'radio'"
                    [checked]="checked"
                    [disabled]="disabled"
                    [value]="value"
                    [ngModel]="model"
                    (ngModelChange)="handleChange(value)"
                />

                <div class="radio-icon" [class.disabled]="disabled"></div>

                <span
                    *ngIf="label"
                    [id]="createId('label')"
                    class="radio-label-contents {{ labelClassName }}"
                >
                    {{ label }}
                </span>
            </label>
        </div>
    `,
    styleUrls: ['./lsq-radio.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqRadioComponent),
            multi: true
        }
    ]
})
export class LsqRadioComponent extends BaseFormControlComponent<any> {
    private static EMPTY_STRING = '';

    @Input() public id: string;
    @Input() public name: string;
    @Input() public label: string;
    @Input() public labelClassName?: string;
    @Input() public disabled = false;
    @Input() public value: any;
    public group: FormGroup;

    public readonly icons = Icons;
    public readonly color = Color;

    constructor(
        public injector: Injector,
        @Optional() private _controlContainer?: ControlContainer
    ) {
        super(injector);
        this.group = this._controlContainer?.control as FormGroup;
    }

    public createId(suffix: string = LsqRadioComponent.EMPTY_STRING): string {
        if (!this.id) {
            return LsqRadioComponent.EMPTY_STRING;
        }
        return this.id + (suffix ? '-' + suffix : LsqRadioComponent.EMPTY_STRING);
    }

    public get checked(): boolean {
        return this.model === this.value;
    }
}
